import { formatUserFacingError } from '@/lib/errors'
import { useEffect, useState } from 'react'
import { adminSubmissionsService } from '@/features/admin/services/admin.submissions.service'

export interface SubmissionStats {
  pending: number
  reviewing: number
  accepted: number
  rejected: number
  total: number
}

const ZERO: SubmissionStats = {
  pending: 0,
  reviewing: 0,
  accepted: 0,
  rejected: 0,
  total: 0,
}

/**
 * Aggregates submission status counts for a given production. Queries by the
 * denormalized productionId field on submissions (set by Cloud Function on write).
 */
export function useProductionSubmissionStats(productionId: string | null) {
  const [stats, setStats] = useState<SubmissionStats>(ZERO)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!productionId) {
      setStats(ZERO)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    return adminSubmissionsService.subscribeByProductionId(
      productionId,
      (subs) => {
        const next: SubmissionStats = { ...ZERO, total: subs.length }
        for (const s of subs) {
          if (s.status === 'pending' || s.status === 'pending_payment') next.pending++
          else if (s.status === 'reviewing') next.reviewing++
          else if (s.status === 'accepted') next.accepted++
          else if (s.status === 'rejected') next.rejected++
        }
        setStats(next)
        setLoading(false)
        setError(null)
      },
      (err: unknown) => {
        setError(formatUserFacingError(err, 'Failed to load stats.'))
        setLoading(false)
      }
    )
  }, [productionId])

  return { stats, loading, error }
}
